import React, { useEffect, useId, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { REACH_LINK_TARGETS, shortDate, toDate, type LinkTarget } from './reach';

/**
 * Write a post → Link (Prototype "Link picker", feedback 3dc485ba): search the
 * site's live pages and published articles, or paste any address. A picked
 * link shows as a card (what it is, its title and address) with Change and
 * Remove. The composer decides what picking does to the picture.
 */

const SHOWN = { pages: 3, articles: 4 };
const SHOWN_SEARCHING = { pages: 4, articles: 5 };
const isUrl = (v: string) => /^https?:\/\/\S+\.\S+/i.test(v.trim());
const bare = (url: string) => url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
const KIND_LABEL = { article: 'Article', page: 'Page' } as const;

export function LinkPicker({
  link,
  target,
  offSite,
  onPick,
  onUseUrl,
  onRemove,
  onClear,
  describedBy,
}: {
  link: string;
  /** What the link is on the site, when it was picked here (or handed in). */
  target: Pick<LinkTarget, 'kind' | 'title'> | null;
  /** "nasa.gov" when the link leaves the site. */
  offSite: string | null;
  onPick: (t: LinkTarget) => void;
  onUseUrl: (url: string) => void;
  /** Remove: no link (a picture that came with it goes too). */
  onRemove: () => void;
  /** Change: pick again (the picture stays until something else replaces it). */
  onClear: () => void;
  describedBy?: string;
}) {
  const { accountId } = useAccount();
  const listId = useId();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [changing, setChanging] = useState(false);
  // Ask as typing settles, not on every key.
  const [asked, setAsked] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setAsked(q.trim()), 200);
    return () => window.clearTimeout(t);
  }, [q]);
  const typedUrl = isUrl(q);
  const { data, loading } = useQuery<{ reachLinkTargets: LinkTarget[] }>(REACH_LINK_TARGETS, {
    variables: { accountId, q: asked },
    skip: !accountId || !open || typedUrl,
    fetchPolicy: 'cache-and-network',
  });

  const groups = useMemo(() => {
    const all = typedUrl ? [] : data?.reachLinkTargets || [];
    const n = asked ? SHOWN_SEARCHING : SHOWN;
    return [
      { head: 'Pages', items: all.filter((t) => t.kind === 'page').slice(0, n.pages) },
      {
        head: asked ? 'Articles' : 'Recently published articles',
        items: all.filter((t) => t.kind === 'article').slice(0, n.articles),
      },
    ].filter((g) => g.items.length);
  }, [data, asked, typedUrl]);
  const options = groups.flatMap((g) => g.items);
  // Row 0 is "Use <address>" when one is typed; the pages and articles follow.
  const count = (typedUrl ? 1 : 0) + options.length;
  useEffect(() => setActive(0), [asked, typedUrl]);

  const choose = (i: number) => {
    if (typedUrl && i === 0) onUseUrl(q.trim());
    else {
      const t = options[i - (typedUrl ? 1 : 0)];
      if (!t) return;
      onPick(t);
    }
    setQ('');
    setOpen(false);
  };

  if (link) {
    return (
      <div className="wp-ln">
        <span className="wp-ln-kind">{target ? KIND_LABEL[target.kind] : 'Link'}</span>
        <div className="wp-ln-text">
          <div>{target ? target.title : bare(link)}</div>
          <div className="wp-ln-url">{offSite ? 'Not on your site' : bare(link)}</div>
        </div>
        <button
          type="button"
          className="ink-quiet-link"
          onClick={() => {
            onClear();
            setChanging(true);
            setOpen(true);
          }}
        >
          Change
        </button>
        <button type="button" className="ink-quiet-link" onClick={onRemove}>
          Remove
        </button>
      </div>
    );
  }

  const optionId = (i: number) => `${listId}-${i}`;
  return (
    <div className="wp-ln-pick">
      <input
        className="ink-site-input"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && count ? optionId(active) : undefined}
        aria-label="Link"
        aria-describedby={describedBy}
        value={q}
        autoFocus={changing}
        placeholder="Search articles and pages, or paste a link"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && count) {
            e.preventDefault();
            setOpen(true);
            setActive((a) => (a + 1) % count);
          } else if (e.key === 'ArrowUp' && count) {
            e.preventDefault();
            setActive((a) => (a - 1 + count) % count);
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (count) choose(active);
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open ? (
        <div className="wp-ln-menu" id={listId} role="listbox" aria-label="Pages and articles">
          {typedUrl ? (
            <div
              id={optionId(0)}
              role="option"
              aria-selected={active === 0}
              className="wp-ln-opt wp-ln-opt-url"
              // mousedown, so the pick lands before the field's blur closes the list
              onMouseDown={(e) => {
                e.preventDefault();
                choose(0);
              }}
            >
              <span className="wp-ln-kind">Link</span>
              <span>Use {q.trim()}</span>
            </div>
          ) : null}
          {groups.map((g) => (
            <div key={g.head} role="group" aria-label={g.head}>
              <div className="wp-ln-head">{g.head}</div>
              {g.items.map((t) => {
                const i = options.indexOf(t) + (typedUrl ? 1 : 0);
                const when = toDate(t.publishedDate);
                return (
                  <div
                    key={`${t.kind}-${t.id}`}
                    id={optionId(i)}
                    role="option"
                    aria-selected={active === i}
                    className="wp-ln-opt"
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(i);
                    }}
                  >
                    <span className="wp-ln-title">{t.title}</span>
                    <span className="wp-ln-meta">
                      {t.kind === 'page'
                        ? `Page · ${bare(new URL(t.url).pathname) || '/'}`
                        : [t.category, when ? shortDate(when) : null].filter(Boolean).join(' · ') ||
                          'Article'}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
          {!typedUrl && !options.length ? (
            <div className="wp-ln-empty">
              {loading
                ? 'Looking…'
                : asked
                  ? 'Nothing published matches. Paste a link to use any address.'
                  : 'Nothing published yet. Paste a link to use any address.'}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
